import { useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, CONTACT } from "@/data/site";
import { MaskedLines, Reveal, Eyebrow } from "@/components/site/Motion";
import { ContactBlock } from "@/components/site/ContactBlock";
import { scrollToId } from "@/components/site/SmoothScroll";
import NotFound from "@/pages/NotFound";

const ServiceHero = ({ s }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  return (
    <section ref={ref} data-testid="service-hero" className="relative flex min-h-[92svh] items-end overflow-hidden pt-20">
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          key={s.slug}
          src={s.hero}
          alt={s.name}
          initial={{ clipPath: "inset(20% 20% 20% 20%)", scale: 1.25 }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
          transition={{ duration: 1.6, ease: [0.76, 0, 0.24, 1] }}
          className="h-full w-full object-cover"
          style={{ objectPosition: s.heroPos ?? "50% 50%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08090B] via-[#08090B]/50 to-[#08090B]/20" />
      </motion.div>
      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-5 pb-14 sm:px-8 lg:px-12 lg:pb-20">
        <p className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-[#C8D484]">{s.index} — {s.kicker}</p>
        <MaskedLines
          key={s.slug}
          testId="service-title"
          lines={s.lines}
          className="font-display text-[17vw] font-semibold uppercase leading-[0.82] tracking-tight sm:text-[13vw] lg:text-[10vw]"
          lineClassName={(i) => (i === 1 ? "font-serif text-[#C8D484] italic normal-case font-normal tracking-normal" : "")}
        />
        <div className="mt-10 flex flex-wrap gap-4">
          <button onClick={() => scrollToId("orcamento")} data-testid="service-quote-button" className="group inline-flex items-center gap-2 rounded-full bg-[#C8D484] px-7 py-4 font-bold text-black transition-transform duration-300 hover:scale-[1.04]">
            Pedir orçamento <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" />
          </button>
          <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="service-whatsapp-button" className="rounded-full border border-white/30 px-7 py-4 font-semibold backdrop-blur-md transition-colors hover:border-[#C8D484] hover:text-[#C8D484]">
            WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
};

const Features = ({ s }) => (
  <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 sm:py-32 lg:px-12">
    <div className="grid gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <Eyebrow>{s.name}</Eyebrow>
        <p data-testid="service-intro" className="mt-6 font-serif text-3xl leading-[1.15] text-white/95 sm:text-[2.6rem]">{s.intro}</p>
      </Reveal>
      <div className="lg:col-span-6 lg:col-start-7">
        {s.features.map((f, i) => (
          <Reveal key={f.t} delay={i * 0.08}>
            <div data-testid={`service-feature-${i}`} className="group grid grid-cols-[3rem_1fr] gap-4 border-t border-white/10 py-8 last:border-b">
              <span className="font-mono text-sm text-[#C8D484]">0{i + 1}</span>
              <div>
                <h3 className="font-display text-3xl font-semibold uppercase transition-transform duration-500 group-hover:translate-x-2">{f.t}</h3>
                <p className="mt-2 text-white/75">{f.d}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Gallery = ({ s }) => (
  <section data-testid="service-gallery" className="mx-auto max-w-[1440px] px-5 pb-24 sm:px-8 sm:pb-32 lg:px-12">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:auto-rows-[260px]">
      {s.gallery.map((g, i) => {
        const src = g?.src ?? g;
        return (
        <Reveal key={src} delay={i * 0.1} className={`h-[360px] md:h-auto ${i === 0 ? "md:col-span-7 md:row-span-2" : i < 3 ? "md:col-span-5" : "md:col-span-6 md:row-span-2"}`}>
          <div className="group h-full overflow-hidden [clip-path:polygon(0_0,100%_0,100%_85%,92%_100%,0_100%)]">
            <img src={src} alt={`${s.name} ${i + 1}`} style={{ objectPosition: g?.pos ?? "50% 35%" }} className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
          </div>
        </Reveal>
        );
      })}
    </div>
  </section>
);

const Others = ({ s }) => (
  <section className="border-t border-white/10">
    <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 lg:px-12">
      <Eyebrow>Outros serviços</Eyebrow>
      <div className="mt-6 flex flex-col">
        {SERVICES.filter((o) => o.slug !== s.slug).map((o) => (
          <Link key={o.slug} to={`/servicos/${o.slug}`} data-testid={`other-service-${o.slug}`} className="group flex items-center justify-between border-b border-white/10 py-6">
            <span className="font-display text-4xl font-semibold uppercase tracking-tight transition-[color,transform] duration-500 group-hover:translate-x-3 group-hover:text-[#C8D484] sm:text-6xl">{o.name}</span>
            <ArrowUpRight className="h-8 w-8 shrink-0 transition-transform duration-500 group-hover:rotate-45 group-hover:text-[#C8D484]" />
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default function ServicePage() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return <NotFound />;
  return (
    <div data-testid={`service-page-${s.slug}`} key={s.slug}>
      <ServiceHero s={s} />
      <Features s={s} />
      <Gallery s={s} />
      <ContactBlock key={s.slug} defaultService={s.slug} title={`${s.name}: pedir orçamento`} />
      <Others s={s} />
    </div>
  );
}
